import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { msmartAxios } from "../../../api/axios";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../utils/errorMessage";

// =========================
// Small UI helpers
// =========================
function SectionCard({ title, right, children }) {
  return (
    <div className="card p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fw-bold">{title}</div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function SkeletonRows({ rows = 6 }) {
  return (
    <div className="table-responsive mt-2">
      <table className="table table-bordered table-sm align-middle">
        <thead className="table-light">
          <tr>
            <th style={{ width: 64 }}>#</th>
            <th>Member</th>
            <th style={{ width: 170 }}>Position</th>
            <th style={{ width: 220 }}>Manager</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i}>
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: 26 }} />
              </td>
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: "70%" }} />
                <div className="ss-skeleton mt-2" style={{ height: 12, borderRadius: 8, width: "35%" }} />
              </td>
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 999, width: 90 }} />
              </td>
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: "60%" }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function posLabel(pos) {
  if (pos === "Manager & Member") return "Team Leader";
  return pos || "-";
}

function posBadgeClass(pos) {
  const p = posLabel(pos);
  if (p === "Manager") return "bg-dark";
  if (p === "Team Leader") return "bg-primary";
  return "bg-secondary";
}

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

export default function ManageTeam() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError } = useToast();

  const [members, setMembers] = useState([]);
  const [managerList, setManagerList] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI filters
  const [q, setQ] = useState("");
  const [posFilter, setPosFilter] = useState("All"); // All | Member | Team Leader | Manager

  // =========================
  // Fetch data
  // =========================
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    Promise.all([
      msmartAxios.get(`/api/msmart/manager/get/team/member/${teamId}`, {
        headers: { accessToken: user.token },
      }),
      msmartAxios.get(`/api/msmart/get/manager/list/${teamId}`, {
        headers: { accessToken: user.token },
      }),
    ])
      .then(([teamRes, mgrRes]) => {
        if (!mounted) return;

        const team = teamRes?.data?.team || [];
        const mgrs = mgrRes?.data || [];

        setMembers(team);
        setManagerList(Array.isArray(mgrs) ? mgrs : []);
      })
      .catch((err) => {
        notifyError(getErrorMessage(err, "Unable to retrieve data"));
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [user.token, teamId, notifyError]);

  // =========================
  // Derived maps & filtered rows
  // =========================
  const managerNameByUsername = useMemo(() => {
    const map = {};
    (managerList || []).forEach((m) => {
      if (m?.username) map[m.username] = m?.nameInTeam || m.username;
    });
    return map;
  }, [managerList]);

  const rows = useMemo(() => {
    const text = q.trim().toLowerCase();

    return (members || [])
      .map((m) => {
        const managerUsername = m.managerUsername || "-";
        const managerDisplay = managerNameByUsername[managerUsername] || managerUsername;

        return {
          ...m,
          positionDisplay: posLabel(m.position),
          managerDisplay,
          managerUsername,
        };
      })
      .filter((m) => {
        // filter position
        if (posFilter !== "All" && m.positionDisplay !== posFilter) return false;

        // filter search
        if (!text) return true;
        const hay = `${m.nameInTeam} ${m.username} ${m.positionDisplay} ${m.managerDisplay}`.toLowerCase();
        return hay.includes(text);
      })
      .sort((a, b) => {
        // nicer sorting: position then name
        const pa = a.positionDisplay || "";
        const pb = b.positionDisplay || "";
        if (pa !== pb) return pa.localeCompare(pb);
        return (a.nameInTeam || "").localeCompare(b.nameInTeam || "");
      });
  }, [members, q, posFilter, managerNameByUsername]);

  const totalMembers = members?.length || 0;

  return (
    <div className="ss-page">
      <div className="container py-6">
        {/* Header */}
        <div className="row justify-content-center text-start">
          <div className="col-lg-12">
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
            Sales Activity Management.
            </div>
          </div>
        </div>

        {/* Top segment (Team / Personal) */}
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="msmart-segment my-4">
              <NavLink to="#" className={() => "msmart-seg-link active"}>
                Team
              </NavLink>

              <NavLink
                to={`/msmart/manager/database/${teamId}`}
                className={({ isActive }) => `msmart-seg-link ${isActive ? "active" : ""}`}
              >
                Personal
              </NavLink>
            </div>
          </div>
        </div>

        {/* Main card */}
        <div className="card text-center mt-4">
          {/* Tabs */}
          <div className="card-header text-start">
            <ul className="nav nav-tabs card-header-tabs flex-wrap">
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/summary/${teamId}`}>
                  Summary
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/summary/${teamId}?view=analysis`}>Analysis</Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link active" to="#">
                  Manage
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/activity/${teamId}`}>
                  Activities
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/followup/${teamId}`}>
                  Follow Up
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/leads/${teamId}`}>
                  Leads
                </Link>
              </li>
            </ul>
          </div>

          {/* Body */}
          <div className="card-body text-start">
            {/* Toolbar */}
            <SectionCard
              title="Team Members"
              right={
                <span className="badge bg-light text-dark">
                  {loading ? "Loading..." : `${rows.length} / ${totalMembers}`}
                </span>
              }
            >
              <div className="row g-2 align-items-end">
                <div className="col-12 col-lg-6">
                  <label className="form-label mb-1">Search</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search" />
                    </span>
                    <input
                      className="form-control"
                      placeholder="Search name, username, manager..."
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                    {q ? (
                      <button className="btn btn-outline-dark" type="button" onClick={() => setQ("")}>
                        Clear
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="col-12 col-lg-3">
                  <label className="form-label mb-1">Position</label>
                  <select className="form-select" value={posFilter} onChange={(e) => setPosFilter(e.target.value)}>
                    <option value="All">All</option>
                    <option value="Member">Member</option>
                    <option value="Team Leader">Team Leader</option>
                    <option value="Manager">Manager</option>
                  </select>
                </div>

                <div className="col-12 col-lg-3 d-flex gap-2 justify-content-lg-end">
                  <button
                    className="btn btn-outline-dark w-100"
                    type="button"
                    onClick={() => {
                      setQ("");
                      setPosFilter("All");
                    }}
                    disabled={loading}
                  >
                    <i className="bi bi-sliders me-2" />
                    Reset
                  </button>

                  <Link className="btn btn-dark w-100" to={`/msmart/team/summary/${teamId}`}>
                    <i className="bi bi-graph-up me-2" />
                    Summary
                  </Link>
                </div>
              </div>
            </SectionCard>

            {/* Table */}
            {loading ? (
              <SkeletonRows rows={7} />
            ) : (
              <div className="table-responsive mt-3" style={{ fontSize: "0.9rem" }}>
                <table className="table table-bordered table-hover table-sm align-middle ss-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 64 }}>#</th>
                      <th className="text-start">Member</th>
                      <th className="text-start" style={{ width: 170 }}>
                        Position
                      </th>
                      <th className="text-start" style={{ width: 220 }}>
                        Manager
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.length ? (
                      rows.map((val, idx) => (
                        <tr key={val.username || idx}>
                          <td className="fw-semibold">{idx + 1}</td>

                          <td className="text-start">
                            <div className="d-flex align-items-center gap-2">
                              <div>
                                <div className="fw-semibold">{val.nameInTeam || "-"}</div>
                                <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                                  @{val.username}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="text-start">
                            <span className={`badge ${posBadgeClass(val.position)} ss-badge`}>
                              {posLabel(val.position)}
                            </span>
                          </td>

                          <td className="text-start">
                            <div className="fw-semibold">{val.managerDisplay || "-"}</div>
                            <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                              @{val.managerUsername || "-"}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No members found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer gap */}
        <div className="mt-3" />
      </div>
    </div>
  );
}
